/** Normaliza filas del API al modelo del reporte tipo PDF (mesero → familia → productos). */

export const MESERO_KEY_GENERAL = '__general__';

/** Título en PDF/Excel cuando la exportación es vista global (totales por familia). */
export const PRODUCTOS_MESERO_TITULO_GLOBAL =
  'REPORTE PRODUCTOS VENDIDOS POR MESERO — VISTA GLOBAL (TOTALES POR FAMILIA)';

/** Título exportaciones para la vista «Global por mesero» (plano por bloque, sin columnas de mesero). */
export const PRODUCTOS_MESERO_TITULO_GLOBAL_POR_MESERO =
  'REPORTE PRODUCTOS VENDIDOS POR MESERO — GLOBAL POR MESERO';

const num = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function normalizeFamilia(rawFamilia) {
  const s = String(rawFamilia ?? '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!s) return 'Sin familias';
  return s;
}

function isGenericFamiliaLabel(value) {
  const v = String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
  return v === 'general' || v === 'global' || v === 'todas' || v === 'todos';
}

function pickFamiliaLikeValueFromAnyKey(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const keyLooksLikeFamilia = (k) => {
    const n = String(k || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return (
      n.includes('familia') ||
      n.includes('categoria') ||
      n.includes('grupo') ||
      n.includes('rubro') ||
      n.includes('linea') ||
      n.includes('clasificacion')
    );
  };
  for (const [k, v] of Object.entries(raw)) {
    if (!keyLooksLikeFamilia(k)) continue;
    const s = String(v ?? '').trim();
    if (!s) continue;
    if (isGenericFamiliaLabel(s)) continue;
    return normalizeFamilia(s);
  }
  return '';
}

function firstNonEmptyString(...vals) {
  for (const v of vals) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  return '';
}

/** Clave estable para fusionar la misma familia entre apartados (mayúsculas / acentos). */
function familiaMergeKey(name) {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

/** Prioriza el campo `familia` del backend (y alias habituales); el resto solo si viene vacío. */
export function familiaLabelFromRaw(raw) {
  if (!raw || typeof raw !== 'object') return 'Sin familias';
  const primary = firstNonEmptyString(
    raw.familia,
    raw.Familia,
    raw.FAMILIA,
    raw.familiaProducto,
    raw.familia_producto,
    raw.familiaNombre,
    raw.familia_nombre
  );
  const primaryNorm = primary ? normalizeFamilia(primary) : '';
  if (primaryNorm && !isGenericFamiliaLabel(primaryNorm)) return primaryNorm;
  const dynamicFamilia = pickFamiliaLikeValueFromAnyKey(raw);
  if (dynamicFamilia) return dynamicFamilia;
  const s = firstNonEmptyString(
    raw.subFamilia,
    raw.subfamilia,
    raw.sub_familia,
    raw.family,
    raw.familyName,
    raw.family_name,
    raw.familiaName,
    raw.familia_name,
    raw.nombre_familia,
    raw.desc_familia,
    raw.descripcion_familia,
    raw.familia_desc,
    raw.nombreFamilia,
    raw.categoria,
    raw.categoriaNombre,
    raw.nombreCategoria,
    raw.subFamilia,
    raw.subfamilia,
    raw.sub_familia,
    raw.familiaReal,
    raw.grupo,
    raw.grupoProducto,
    raw.linea,
    raw.clasificacion,
    raw.descFamilia,
    raw.descripcionFamilia,
    raw.familiaDescripcion,
    raw.rubro,
    raw.tipoFamilia
  );
  if (s) return normalizeFamilia(s);
  // Si no hubo alternativa real, conservar el valor original (p.ej. "General")
  // evita que todo caiga en "Sin familias" cuando el backend no envía más campos.
  if (primaryNorm) return primaryNorm;
  return 'Sin familias';
}

function isSinFamiliaBucketName(name) {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return false;
  if (/^sin\s+famil/i.test(n)) return true;
  return (
    n === 'sin familias' ||
    n.startsWith('sin familias') ||
    n === 'sin categoría' ||
    n === 'sin categoria' ||
    n.startsWith('sin categoría') ||
    n.startsWith('sin categoria')
  );
}

function sortProductRows(rows, meseroRowOrder = 'default') {
  if (meseroRowOrder === 'importeDesc') {
    return [...rows].sort((a, b) => {
      const d = (Number(b.importe) || 0) - (Number(a.importe) || 0);
      if (d !== 0) return d;
      const dq = (Number(b.cantidad) || 0) - (Number(a.cantidad) || 0);
      if (dq !== 0) return dq;
      const c = String(a.codigo).localeCompare(String(b.codigo), 'es', {
        numeric: true,
        sensitivity: 'base',
      });
      if (c !== 0) return c;
      return String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' });
    });
  }
  if (meseroRowOrder === 'cantidadDesc') {
    return [...rows].sort((a, b) => {
      const d = (Number(b.cantidad) || 0) - (Number(a.cantidad) || 0);
      if (d !== 0) return d;
      const di = (Number(b.importe) || 0) - (Number(a.importe) || 0);
      if (di !== 0) return di;
      const c = String(a.codigo).localeCompare(String(b.codigo), 'es', {
        numeric: true,
        sensitivity: 'base',
      });
      if (c !== 0) return c;
      return String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' });
    });
  }
  return [...rows].sort((a, b) => {
    const c = String(a.codigo).localeCompare(String(b.codigo), 'es', {
      numeric: true,
      sensitivity: 'base',
    });
    if (c !== 0) return c;
    return String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' });
  });
}

function compareFamiliaGroups(a, b, famOrder = 'default') {
  const au = isSinFamiliaBucketName(a.familia) ? 1 : 0;
  const bu = isSinFamiliaBucketName(b.familia) ? 1 : 0;
  if (au !== bu) return au - bu;
  if (famOrder === 'importeDesc') {
    const d = (b.totals.importe ?? 0) - (a.totals.importe ?? 0);
    if (d !== 0) return d;
  } else if (famOrder === 'cantidadDesc') {
    const d = (b.totals.cantidad ?? 0) - (a.totals.cantidad ?? 0);
    if (d !== 0) return d;
  }
  return a.familia.localeCompare(b.familia, 'es', { sensitivity: 'base' });
}

function isTruthyFlag(v) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'si' || s === 'sí';
  }
  return false;
}

function normalizeTipoProducto(v) {
  return String(v ?? '')
    .trim()
    .toUpperCase();
}

function apartadoKeyFromRow(row) {
  if (isTruthyFlag(row.esBebidas)) return 'bebidas';
  const tipo = normalizeTipoProducto(row.tipoProducto);
  if (tipo === 'BEBIDA' || tipo === 'BEBIDAS') return 'bebidas';
  if (tipo === 'ALIMENTO' || tipo === 'ALIMENTOS') return 'alimentos';
  return 'otros';
}

/** Un solo bloque: agrupa solo por `familia`, sin rebanar antes por tipo de producto. */
const APARTADO_TODAS_FAMILIAS = '__todas_familias__';

function apartadoLabel(key) {
  if (key === APARTADO_TODAS_FAMILIAS) return 'FAMILIAS';
  if (key === 'bebidas') return 'BEBIDAS';
  if (key === 'alimentos') return 'ALIMENTOS';
  return 'OTROS PRODUCTOS';
}

function sortApartados(a, b) {
  const order = { [APARTADO_TODAS_FAMILIAS]: -1, bebidas: 0, alimentos: 1, otros: 2 };
  return (order[a.key] ?? 99) - (order[b.key] ?? 99);
}

/** Convierte el valor del formulario de Reportes a `meseroGroupOrder` para {@link buildProductosMeseroTree}. */
export function ordenMeserosFormToGroupOrder(ordenMeseros) {
  switch (String(ordenMeseros || '')) {
    case 'ventas-desc':
      return 'importeDesc';
    case 'ventas-asc':
      return 'importeAsc';
    case 'codigo-asc':
      return 'codigoAsc';
    case 'codigo-desc':
      return 'codigoDesc';
    case 'nombre-asc':
      return 'nombreAsc';
    case 'nombre-desc':
      return 'nombreDesc';
    default:
      return 'default';
  }
}

function compareMeseroPorCodigo(a, b) {
  const na = num(a.meseroSortId);
  const nb = num(b.meseroSortId);
  if (na != null && nb != null && na !== nb) return na < nb ? -1 : 1;
  if (na != null && nb == null) return -1;
  if (na == null && nb != null) return 1;
  const sa = String(a.meseroSortId ?? '').trim();
  const sb = String(b.meseroSortId ?? '').trim();
  if (sa !== sb) return sa.localeCompare(sb, 'es', { numeric: true, sensitivity: 'base' });
  return compareMeseroPorNombre(a, b);
}

function compareMeseroPorNombre(a, b) {
  const sa = String(a.meseroSortNombre ?? '').trim() || String(a.label ?? '');
  const sb = String(b.meseroSortNombre ?? '').trim() || String(b.label ?? '');
  return sa.localeCompare(sb, 'es', { sensitivity: 'base' });
}

export function normalizeProductosMeseroRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const costoUnit = num(raw.costo ?? raw.costoUnitario);
  const cantidad = num(raw.cantidad) ?? 0;
  let totalCosto = num(raw.totalCosto ?? raw.tCosto ?? raw.costoTotal);
  if (totalCosto == null && costoUnit != null && cantidad != null) {
    totalCosto = costoUnit * cantidad;
  }
  const precio = num(raw.precio ?? raw.precioUnitario);
  let importe = num(raw.importe ?? raw.total);
  if (importe == null && precio != null && cantidad != null) {
    importe = precio * cantidad;
  }
  let utilidad = num(raw.utilidad);
  if (utilidad == null && importe != null && totalCosto != null) {
    utilidad = importe - totalCosto;
  }

  const nombreBase = raw.nombre ?? raw.nombreProducto ?? '';
  const desc = raw.descripcion ?? '';
  const nombre = desc && nombreBase ? `${nombreBase} (${desc})` : nombreBase || desc || '-';

  const subFromRaw = firstNonEmptyString(
    raw.subFamilia,
    raw.subfamilia,
    raw.sub_familia
  );
  const familia = familiaLabelFromRaw(raw);

  return {
    meseroId: raw.meseroId ?? raw.mesero ?? raw.idMesero ?? null,
    meseroNombre: String(raw.meseroNombre ?? raw.nombreMesero ?? '').trim(),
    tCheques: num(raw.tCheques ?? raw.totalCheques),
    tPax: num(raw.tPax ?? raw.totalPax),
    familia,
    subFamilia: subFromRaw,
    codigo: String(raw.codigo ?? raw.code ?? '-').trim() || '-',
    nombre,
    cantidad,
    costo: costoUnit,
    totalCosto,
    precio,
    importe,
    utilidad,
    tipoProducto: raw.tipoProducto ?? raw.tipo_producto ?? null,
    esBebidas: raw.es_bebidas ?? raw.esBebidas ?? raw.bebida ?? null,
  };
}

function meseroKey(row) {
  const id = row.meseroId;
  const nom = row.meseroNombre;
  if (id != null && id !== '') return `id:${id}`;
  if (nom) return `nom:${nom}`;
  return MESERO_KEY_GENERAL;
}

function meseroLabel(row) {
  const id = row.meseroId;
  const nom = row.meseroNombre;
  if (id != null && id !== '' && nom) return `${id} ${nom}`;
  if (id != null && id !== '') return String(id);
  if (nom) return nom;
  return 'General';
}

/**
 * @param {object[]} rows
 * @param {{
 *   meseroRowOrder?: 'default' | 'importeDesc' | 'cantidadDesc';
 *   meseroGroupOrder?: 'default' | 'importeDesc' | 'importeAsc' | 'codigoAsc' | 'codigoDesc' | 'nombreAsc' | 'nombreDesc';
 *   sinApartado?: boolean;
 * }} [options]
 * `sinApartado`: vista global compacta — totales por `familia` del API, sin agrupar antes por BEBIDAS/ALIMENTOS/OTROS.
 */
export function buildProductosMeseroTree(rows, options = {}) {
  const meseroRowOrder = options.meseroRowOrder ?? 'default';
  const meseroGroupOrder = options.meseroGroupOrder ?? 'default';
  const sinApartado = options.sinApartado === true;
  const famOrder =
    meseroRowOrder === 'importeDesc'
      ? 'importeDesc'
      : meseroRowOrder === 'cantidadDesc'
        ? 'cantidadDesc'
        : 'default';

  const list = (Array.isArray(rows) ? rows : [])
    .map(normalizeProductosMeseroRow)
    .filter(Boolean);

  const meseroMap = new Map();
  for (const row of list) {
    const key = meseroKey(row);
    if (!meseroMap.has(key)) {
      meseroMap.set(key, {
        key,
        label: meseroLabel(row),
        meseroSortId: row.meseroId,
        meseroSortNombre: (row.meseroNombre || '').trim(),
        tCheques: row.tCheques,
        tPax: row.tPax,
        apartados: new Map(),
      });
    }
    const m = meseroMap.get(key);
    if (row.tCheques != null) m.tCheques = row.tCheques;
    if (row.tPax != null) m.tPax = row.tPax;
    if (
      (m.meseroSortNombre == null || m.meseroSortNombre === '') &&
      row.meseroNombre &&
      String(row.meseroNombre).trim()
    ) {
      m.meseroSortNombre = String(row.meseroNombre).trim();
    }
    if ((m.meseroSortId == null || m.meseroSortId === '') && row.meseroId != null && row.meseroId !== '') {
      m.meseroSortId = row.meseroId;
    }

    const apartadoKey = sinApartado ? APARTADO_TODAS_FAMILIAS : apartadoKeyFromRow(row);
    if (!m.apartados.has(apartadoKey)) {
      m.apartados.set(apartadoKey, {
        key: apartadoKey,
        label: apartadoLabel(apartadoKey),
        familias: new Map(),
      });
    }
    const apartado = m.apartados.get(apartadoKey);

    const famKey = row.familia;
    if (!apartado.familias.has(famKey)) {
      apartado.familias.set(famKey, {
        familia: row.familia,
        subFamilia: null,
        rows: [],
        totals: { cantidad: 0, totalCosto: 0, importe: 0, utilidad: 0 },
      });
    }
    const fam = apartado.familias.get(famKey);
    fam.rows.push(row);
    fam.totals.cantidad += row.cantidad || 0;
    fam.totals.totalCosto += row.totalCosto || 0;
    fam.totals.importe += row.importe || 0;
    fam.totals.utilidad += row.utilidad || 0;
  }

  const meseros = [];
  for (const m of meseroMap.values()) {
    const apartados = [...m.apartados.values()]
      .map((ap) => {
        for (const f of ap.familias.values()) {
          f.rows = sortProductRows(f.rows, meseroRowOrder);
        }
        const familias = [...ap.familias.values()].sort((a, b) =>
          compareFamiliaGroups(a, b, famOrder)
        );
        return { ...ap, familias };
      })
      .sort(sortApartados);
    let mt = { cantidad: 0, totalCosto: 0, importe: 0, utilidad: 0 };
    for (const ap of apartados) {
      for (const f of ap.familias) {
        mt.cantidad += f.totals.cantidad;
        mt.totalCosto += f.totals.totalCosto;
        mt.importe += f.totals.importe;
        mt.utilidad += f.totals.utilidad;
      }
    }
    meseros.push({
      ...m,
      apartados,
      meseroTotals: mt,
    });
  }

  meseros.sort((a, b) => {
    if (a.key === MESERO_KEY_GENERAL && b.key !== MESERO_KEY_GENERAL) return 1;
    if (b.key === MESERO_KEY_GENERAL && a.key !== MESERO_KEY_GENERAL) return -1;
    if (meseroGroupOrder === 'importeDesc') {
      const d = (Number(b.meseroTotals?.importe) || 0) - (Number(a.meseroTotals?.importe) || 0);
      if (d !== 0) return d;
    } else if (meseroGroupOrder === 'importeAsc') {
      const d = (Number(a.meseroTotals?.importe) || 0) - (Number(b.meseroTotals?.importe) || 0);
      if (d !== 0) return d;
    } else if (meseroGroupOrder === 'codigoAsc') {
      const c = compareMeseroPorCodigo(a, b);
      if (c !== 0) return c;
    } else if (meseroGroupOrder === 'codigoDesc') {
      const c = compareMeseroPorCodigo(b, a);
      if (c !== 0) return c;
    } else if (meseroGroupOrder === 'nombreAsc') {
      const c = compareMeseroPorNombre(a, b);
      if (c !== 0) return c;
    } else if (meseroGroupOrder === 'nombreDesc') {
      const c = compareMeseroPorNombre(b, a);
      if (c !== 0) return c;
    }
    return a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
  });
  return meseros;
}

/** Une familias con el mismo nombre entre ALIMENTOS / BEBIDAS / OTROS (vista global). */
export function mergeFamiliasGlobales(mesero) {
  const famMap = new Map();
  for (const ap of mesero.apartados) {
    for (const fam of ap.familias) {
      const label = String(fam.familia ?? 'Sin categoría');
      const key = familiaMergeKey(label);
      if (!famMap.has(key)) {
        famMap.set(key, {
          familia: label,
          totals: {
            cantidad: Number(fam.totals.cantidad) || 0,
            totalCosto: Number(fam.totals.totalCosto) || 0,
            importe: Number(fam.totals.importe) || 0,
            utilidad: Number(fam.totals.utilidad) || 0,
          },
        });
      } else {
        const agg = famMap.get(key);
        agg.totals.cantidad += Number(fam.totals.cantidad) || 0;
        agg.totals.totalCosto += Number(fam.totals.totalCosto) || 0;
        agg.totals.importe += Number(fam.totals.importe) || 0;
        agg.totals.utilidad += Number(fam.totals.utilidad) || 0;
        if (label.length > String(agg.familia).length) agg.familia = label;
      }
    }
  }
  return Array.from(famMap.values());
}

/** Orden de filas al mostrar familias globales fusionadas (respeta mayor ingreso/venta). */
export function sortMergedFamiliasGlobales(familias, meseroRowOrder = 'default') {
  const famOrder =
    meseroRowOrder === 'importeDesc'
      ? 'importeDesc'
      : meseroRowOrder === 'cantidadDesc'
        ? 'cantidadDesc'
        : 'default';
  return [...familias].sort((a, b) => compareFamiliaGroups(a, b, famOrder));
}
